import { DropdownOption } from '../components/common/Dropdown'
import { parseISOTimeString, parseTimeString } from './timeStringParser'

export function formatTime(t: {
  hour?: DropdownOption
  minute?: DropdownOption
  ampm?: DropdownOption
}) {
  const h = t.hour?.label ?? '00'
  const m = t.minute?.label ?? '00'
  const ap = t.ampm?.label ?? ''
  return ap ? `${h}:${m} ${ap}` : `${h}:${m}`
}

export const hoursOptions: DropdownOption[] = Array.from(
  { length: 12 },
  (_, i) => {
    const h = String(i + 1)
    return { id: `h-${h}`, label: h, value: h }
  }
)

export const minutesOptions: DropdownOption[] = [
  { id: 'm-00', label: '00', value: '00' },
  { id: 'm-15', label: '15', value: '15' },
  { id: 'm-30', label: '30', value: '30' },
  { id: 'm-45', label: '45', value: '45' },
]

export const ampmOptions: DropdownOption[] = [
  { id: 'am', label: 'AM', value: 'AM' },
  { id: 'pm', label: 'PM', value: 'PM' },
]

/**
 * Validate date format MM/DD/YYYY (also accepts single-digit month/day
 * such as M/D/YYYY so partial input before blur-padding is not flagged).
 * @param dateStr - Date string to validate
 * @returns true if valid or empty, false otherwise
 */
export const isValidDateFormat = (dateStr: string): boolean => {
  if (!dateStr) return true // Empty is valid
  const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}$/
  return dateRegex.test(dateStr)
}

/**
 * Returns true if a MM/DD/YYYY date string represents a date strictly before today.
 * Assumes the input has already passed isValidDateFormat.
 * @param dateStr - Date string in MM/DD/YYYY format
 */
export const isPastDate = (dateStr: string): boolean => {
  const parts = dateStr.split('/')
  if (parts.length !== 3) return false
  const parsed = new Date(
    Number.parseInt(parts[2]),
    Number.parseInt(parts[0]) - 1,
    Number.parseInt(parts[1])
  )
  parsed.setHours(0, 0, 0, 0)
  const todayMidnight = new Date()
  todayMidnight.setHours(0, 0, 0, 0)
  return parsed < todayMidnight
}

/**
 * Returns true when an ISO calendar date is before today in the browser's local timezone.
 * Date-only values must not be passed to the Date constructor because JavaScript parses them as UTC.
 */
export const isISODateInPast = (dateStr?: string): boolean => {
  if (!dateStr) return false

  const calendarDate = /^\d{4}-\d{2}-\d{2}/.exec(dateStr)?.[0]
  if (!calendarDate) return false

  const now = new Date()
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')

  return calendarDate < today
}

/**
 * Convert date from MM/DD/YYYY format to YYYY-MM-DD format
 * @param dateStr - Date string in MM/DD/YYYY format
 * @returns Date string in YYYY-MM-DD format, or original string if parsing fails
 */
export const convertMMDDYYYYToYYYYMMDD = (dateStr: string): string => {
  const parts = dateStr.split('/')
  if (parts.length === 3) {
    const [mm, dd, yyyy] = parts
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
  }
  return dateStr
}

/**
 * Today's date as a YYYY-MM-DD string, built from the browser's local date
 * parts (getFullYear/getMonth/getDate). Never parse this through
 * `new Date(isoString)` — a timezone-less date-only string parses as UTC
 * midnight, which disagrees with a local "now" by the viewer's UTC offset.
 */
export const getTodayYYYYMMDD = (): string => {
  const now = new Date()
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
}

/**
 * Returns true if a request date (accepts YYYY-MM-DD or MM/DD/YYYY, or a
 * "start - end" range of either — see transformApiResponse in
 * Requestor/timeOffDetailsSlice.ts, which builds `todate` that way for
 * multi-day requests) contains any date strictly before today, using plain
 * string comparison so the result never depends on the viewer's timezone.
 * The start of a range is checked (dates are chronological, so if the start
 * is in the past the range contains a past day even when it ends in the
 * future) — a request that has already begun should no longer be
 * withdrawable/resubmittable. Comparing `new Date(d.date)` (UTC for a
 * date-only string) against a local `new Date()` caused approvers west of
 * UTC (e.g. EST/EDT) to see today's requests wrongly flagged as past for a
 * few hours after their local midnight — see git history on
 * RequestDetailsActions.tsx / RequestDetailsView.tsx for the incident.
 */
export const isRequestDateBeforeToday = (dateStr?: string): boolean => {
  if (!dateStr) return false
  const firstDate = dateStr.includes(' - ')
    ? dateStr.split(' - ')[0]?.trim()
    : dateStr
  if (!firstDate) return false
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(firstDate)
    ? firstDate
    : convertMMDDYYYYToYYYYMMDD(firstDate)
  return iso < getTodayYYYYMMDD()
}

/**
 * Convert UTC ISO date string to 12-hour format with AM/PM
 * @param isoDateString - ISO date string (e.g., "2026-05-29T01:00:00.000Z")
 */
export const formatUTCTo12Hour = (isoDateString: string): string => {
  try {
    const date = new Date(isoDateString)

    // Extract UTC hours and minutes
    const utcHours = date.getUTCHours()
    const utcMinutes = date.getUTCMinutes()

    // Determine AM or PM based on 24-hour clock
    const ampm = utcHours >= 12 ? 'PM' : 'AM'

    // Convert hours to 12-hour format and handle midnight (0) as 12
    const hours12 = utcHours % 12 || 12

    // Pad minutes with a leading zero if they are less than 10
    const minutesPadded = String(utcMinutes).padStart(2, '0')

    // Combine into final string
    return `${hours12}:${minutesPadded} ${ampm}`
  } catch (error) {
    console.error('Error formatting UTC time:', error)
    return 'Invalid time'
  }
}

/**
 * Convert 12-hour time format with AM/PM to 24-hour format
 * @param hour - Hour (1-12)
 * @param minute - Minute (0-59)
 * @param ampm - AM or PM
 * @returns Time string in 24-hour format (HH:MM)
 */
export const convertTo24HourFormat = (
  hour: string,
  minute: string,
  ampm: string
): string => {
  try {
    let hours24 = Number.parseInt(hour, 10)
    const minutes = String(minute).padStart(2, '0')

    if (ampm === 'PM' && hours24 !== 12) {
      hours24 += 12
    } else if (ampm === 'AM' && hours24 === 12) {
      hours24 = 0
    }

    return `${String(hours24).padStart(2, '0')}:${minutes}`
  } catch (error) {
    console.error('Error converting to 24-hour format:', error)
    return '00:00'
  }
}

/**
 * Format date to MM/DD/YYYY format from any common format
 * @param dateStr - Date string in any format (MM/DD/YYYY, YYYY-MM-DD, etc.)
 * @returns Date string in MM/DD/YYYY format
 */
export const formatDateToMMDDYYYY = (dateStr: string): string => {
  if (!dateStr) return ''

  // If already in MM/DD/YYYY format, return as-is
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    return dateStr
  }

  // If in YYYY-MM-DD format, convert to MM/DD/YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-')
    return `${month}/${day}/${year}`
  }

  // Try to parse and format any other date format
  try {
    const date = new Date(dateStr)
    if (!Number.isNaN(date.getTime())) {
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()
      return `${month}/${day}/${year}`
    }
  } catch {
    // Silently fail and return empty string
  }

  return ''
}

/**
 * Format a seniority date (YYYY-MM-DD or YYYY-M-D) to zero-padded MM/DD/YYYY, preserving "NA".
 * @param value - Seniority date string from the API, or "NA"
 * @returns Zero-padded MM/DD/YYYY string, "NA", or the original value if unparseable
 */
export const formatSeniorityDate = (value: string): string => {
  if (!value || value.trim().toUpperCase() === 'NA') return 'NA'

  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value.trim())
  if (!match) return value

  const [, year, month, day] = match
  return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`
}

/**
 * Format ISO date string to MM/DD/YY HH:MM:SS AM/PM timezone format (with seconds)
 * @param isoDateString - ISO date string (e.g., "2026-06-29T22:47:45.379Z")
 * @returns Formatted string like "06/29/26 10:47:45 PM GMT+05:30"
 */
export const formatDateTimeWithTimezoneAndSeconds = (isoDateString: string): string => {
  if (!isoDateString) return ''

  try {
    const date = new Date(isoDateString)

    // Format date as MM/DD/YY
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2) // Last 2 digits of year

    // Format time as HH:MM:SS AM/PM
    const hours = date.getHours()
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const hours12 = hours % 12 || 12

    // Format timezone as short name (PST/PDT) or GMT offset
    const timezoneName =
      new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''

    return `${month}/${day}/${year} ${String(hours12).padStart(2, '0')}:${minutes}:${seconds} ${ampm} ${timezoneName}`.trim()
  } catch (error) {
    console.error('Error formatting date with timezone and seconds:', error)
    return ''
  }
}

/**
 * Format ISO date string to MM/DD/YY HH:MM AM/PM GMT+offset format
 * @param isoDateString - ISO date string (e.g., "2026-06-29T22:47:45.379Z")
 * @returns Formatted string like "06/29/26 10:47 PM GMT+05:30"
 */
export const formatDateTimeWithTimezone = (isoDateString: string): string => {
  if (!isoDateString) return ''

  try {
    const date = new Date(isoDateString)

    // Format date as MM/DD/YY
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2) // Last 2 digits of year

    // Format time as HH:MM AM/PM
    const hours = date.getHours()
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const hours12 = hours % 12 || 12

    // Legacy formats the zone as a short name (PST/PDT via Java's `z`
    // pattern), not a GMT offset — match that instead of "GMT-08:00".
    const timezoneName =
      new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''

    return `${month}/${day}/${year} ${String(hours12).padStart(2, '0')}:${minutes} ${ampm} ${timezoneName}`.trim()
  } catch (error) {
    console.error('Error formatting date with timezone:', error)
    return ''
  }
}

/**
 * Convert a MM/DD/YYYY date string to the legacy "YYYY-MM-DD 00:00:00"
 * format expected by the manual-request-received-date field.
 * @param dateStr - Date string in MM/DD/YYYY format
 */
export const formatManualRequestReceivedDate = (
  dateStr: string
): string => {
  if (!dateStr) return ''

  const yyyyMMdd = convertMMDDYYYYToYYYYMMDD(dateStr)

  return `${yyyyMMdd} 00:00:00`
}

export const formatTimesDisplay = (timesStr?: string): string => {
  if (!timesStr || timesStr === 'Full Day') return 'Full Day'

  // Try to parse as formatted time string first
  const parsed = parseTimeString(timesStr)
  if (parsed) {
    return `${parsed.from.hour}:${parsed.from.minute} ${parsed.from.ampm} - ${parsed.to.hour}:${parsed.to.minute} ${parsed.to.ampm}`
  }

  // Try parsing as ISO time range
  const isoMatch = /^(\d{4}-\d{2}-\d{2}T[\d:.]+Z)\s-\s(\d{4}-\d{2}-\d{2}T[\d:.]+Z)$/.exec(timesStr)
  if (isoMatch) {
    const startTimeStr = parseISOTimeString(isoMatch[1])
    const endTimeStr = parseISOTimeString(isoMatch[2])
    if (startTimeStr && endTimeStr) {
      return `${startTimeStr} - ${endTimeStr}`
    }
  }

  return timesStr
}

export const formatLegacyDateTime = (isoDateString: string): string => {
  if (!isoDateString) return ''

  const formatted = new Date(isoDateString)
    .toLocaleString('en-US', { hour12: true })
    .replace(',', '')

  // Zero-pad only the leading M/D/YYYY date part; time portion is left as-is.
  return formatted.replace(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})/,
    (_match, month: string, day: string, year: string) =>
      `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`
  )
}

export const formatDateToMMDDYY = (dateStr: string): string => {
  if (!dateStr) return ''

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr)
  if (!match) return dateStr

  const [, year, month, day] = match
  return `${month}/${day}/${year.slice(-2)}`
}

export const generateFileName = (): string => {
  const now = new Date()
  const timestamp = [
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    now.getFullYear(),
  ].join('')

  return `TORT ${timestamp}.xlsx`
}